import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isWithinInterval from 'date-fns/isWithinInterval';
import subHours from 'date-fns/subHours';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import { Icon } from '@dnb/eufemia/components';
import Card from '@dnb/eufemia/components/card/Card';
import {getIncidentStatusIcon } from '../../helpers';

import './incident-viewer.css';
import { useTranslation } from 'react-i18next';

/**
 * IncidentViewer - A reusable component for displaying incidents with different filter types
 * 
 * @param {Array} incidents - Array of all incidents
 * @param {String} filterType - Type of filter: 'ongoing', 'historic', 'hours', 'days'
 * @param {Number} timeRange - Number of hours/days for time-based filters
 * @param {String} title - Optional title for the section
 * @param {String} className - Optional additional CSS class
 */
export default function IncidentViewer({ 
  incidents = [], 
  filterType = 'ongoing',
  timeRange = 48,
  title,
  className = ''
}) {
  const { t, i18n } = useTranslation();
  const [filteredIncidents, setFilteredIncidents] = useState([]);

  // Helper function to handle bilingual text (Norwegian||English)
  const findLanguage = (text) => {
    if (!text) return '';
    
    const splitter = text.indexOf('||');
    
    if (splitter !== -1) {
      // If the detected language is norwegian we select the left side of the string
      if (i18n.language === 'nb-NO' || i18n.language === 'no' || i18n.language === 'nb') {
        return text.substring(0, splitter);
      }
      // Otherwise select the right side (English)
      else {
        return text.substring(splitter + 2);
      }
    }
    
    return text;
  };

  // Helper function to get status text - only four options
  const getStatusText = (status) => {
    switch (status) {
      case 'operational':
      case 'none':
        return t('Operational');
      case 'minor_outage':
      case 'partial_outage':
      case 'minor':  
        return t('Partial outage');
      case 'major_outage':
      case 'critical':
      case 'major':  
        return t('Major outage');
      case 'degraded_performance':
        return t('Degraded performance');
      default:
        return status;
    }
  };

  useEffect(() => {
    if (!incidents || !Array.isArray(incidents)) {
      setFilteredIncidents([]);
      return;
    }

    let filtered = [];

    switch (filterType) {
      case 'ongoing':
        // Only unresolved incidents
        filtered = incidents.filter((e) => e.status !== 'resolved');
        break;

      case 'historic':
        // Only resolved incidents
        filtered = incidents.filter((e) => e.status === 'resolved');
        break;

      case 'hours':
        // Incidents within the last X hours
        {
          let now = new Date();
          let earliestDate = subHours(now, timeRange);
          filtered = incidents.filter((e) => {
            if (e.status !== 'resolved') {
              return true;
            } else {
              return isWithinInterval(new Date(e.updated_at), { 
                start: earliestDate, 
                end: now 
              });
            }
          });
        }
        break;

      case 'days':
        // Incidents within the last X days
        {
          let now = new Date();
          let earliestDate = subDays(now, timeRange);
          filtered = incidents.filter((e) => {
            if (e.status !== 'resolved') {
              return true;
            } else {
              return isWithinInterval(new Date(e.updated_at), { 
                start: earliestDate, 
                end: now 
              });
            }
          });
        }
        break;

      case 'all':
        // All incidents
        filtered = incidents;
        break;

      default:
        filtered = incidents.filter((e) => e.status !== 'resolved');
    }

    setFilteredIncidents(filtered);
  }, [incidents, filterType, timeRange]);

  const renderIncidentCard = (incident) => {
    try {
      const startedAt = incident.started_at ? format(new Date(incident.started_at), 'dd.MM.yyyy, HH:mm') : 'N/A';
      const latestUpdate = incident.incident_updates && incident.incident_updates.length > 0 
        ? incident.incident_updates[0] 
        : null;

      // Get affected components
      const affectedComponents = incident.components && incident.components.length > 0
        ? incident.components.map(c => t(c.name || c)).join(', ')
        : '';

      const impactStatus = incident.impact || 'none';

      return (
        <Card 
          key={incident.id} 
          className={"incident-viewer__card"} 
          outline='none'
        >
          <div className="incident-viewer__card-content">
            <div className="incident-viewer__card-header">
              <div className="incident-viewer__card-start">
                <span className="incident-viewer__label">{t('Incident started')}: {startedAt}</span>
              </div>
            </div>

            <div className="incident-viewer__right-section">
              <div className={`incident-viewer__status-container incident-viewer__status-container--${impactStatus}`}>
                <span className={`incident-viewer__icon-circle incident-viewer__icon-circle--${impactStatus}`}>
                  <Icon icon={getIncidentStatusIcon(impactStatus)} size="16" />
                </span>
                <span className={`incident-viewer__status-text`}>
                  {getStatusText(impactStatus)}
                </span>
              </div>
            </div>

            <h3 className="incident-viewer__title">{incident.name ? findLanguage(incident.name) : 'Incident'}</h3>

            {affectedComponents && (
              <div className={`incident-viewer__affected incident-viewer__affected--${impactStatus}`}>
                <span className="incident-viewer__affected-label">{t('Incident affects')}: {affectedComponents}</span>
              </div>
            )}

            {latestUpdate && latestUpdate.body && (
              <div className="incident-viewer__description">
                {findLanguage(latestUpdate.body)}
              </div>
            )}

            {incident.incident_updates && incident.incident_updates.length > 1 && (
              <div className="incident-viewer__guidance">
                <strong>{t('Customer Guidance')}:</strong>
                <p>{t("If you're unable to log in, please use web banking or contact support at 800 123 456. We apologize for the inconvenience and appreciate your patience.")}</p>
              </div>
            )}
          </div>
        </Card>
      );
    } catch (error) {
      console.error('Error rendering incident card:', error, incident);
      return null;
    }
  };

  return (
    <div className={`incident-viewer ${className}`}>
      {title && (
        <h2 className="incident-viewer__section-title">{title}</h2>
      )}
      
      {filteredIncidents.length > 0 ? (
        <div className="incident-viewer__list">
          {filteredIncidents.map(incident => renderIncidentCard(incident))}
        </div>
      ) : (
        <Card 
          className="incident-viewer__empty-card" 
          outline='none'
          backgroundColor="pistachio"
        >
          <div className="incident-viewer__empty-content">
            <h1 className="incident-viewer__empty-title">
              {filterType === 'ongoing' 
                ? t('All systems are currently operating normally.')
                : t('No resolved incidents in the archive.')
              }
            </h1>
          </div>
        </Card>
      )}
    </div>
  );
}

IncidentViewer.propTypes = {
  incidents: PropTypes.array,
  filterType: PropTypes.oneOf(['ongoing', 'historic', 'hours', 'days', 'all']),
  timeRange: PropTypes.number,
  title: PropTypes.string,
  className: PropTypes.string,
};
